// src/components/tutorials/JS_String_Templates.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_String_Templates() {
  const refs = {
    basic: useRef(null),
    multiline: useRef(null),
    expressions: useRef(null),
    functions: useRef(null),
    tagged: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "basic", label: "Basic Template Literals" },
    { key: "multiline", label: "Multi-line Strings" },
    { key: "expressions", label: "Expressions in Templates" },
    { key: "functions", label: "Function Calls in Templates" },
    { key: "tagged", label: "Tagged Templates" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">String Template Literals</h1>

      <p className="tutorial-intro">
        Template literals in JavaScript allow you to use strings with embedded
        expressions, multi-line formatting, and dynamic content using backticks
        (<code>`</code>).
      </p>

      {/* Key Points Section */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.4rem", margin: 0 }}>
          Template Literals Key Features
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click a topic below to see interactive examples 👇
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Basic Template Literals */}
      <div ref={refs.basic} className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Template Literals</h2>
        <p>
          Template literals use <code>backticks (`)</code> instead of quotes.
          They make string creation simpler and more readable.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Sanjana";
let greeting = \`Hello, \${name}!\`;
console.log(greeting); // Hello, Sanjana!`}
          />
          <TryItButton
            code={`let name = "Sanjana";
let greeting = \`Hello, \${name}!\`;
console.log(greeting);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>Hello, Sanjana!</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Multi-line Strings */}
      <div ref={refs.multiline} className="tutorial-section">
        <h2 className="section-title">2️⃣ Multi-line Strings</h2>
        <p>
          With backticks, you can easily create multi-line strings without using
          escape characters.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let poem = \`
Roses are red,
Violets are blue,
JavaScript is awesome,
And so are you!
\`;
console.log(poem);`}
          />
          <TryItButton
            code={`let poem = \`
Roses are red,
Violets are blue,
JavaScript is awesome,
And so are you!
\`;
console.log(poem);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>
            {`Roses are red,
Violets are blue,
JavaScript is awesome,
And so are you!`}
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Expressions in Templates */}
      <div ref={refs.expressions} className="tutorial-section">
        <h2 className="section-title">3️⃣ Expressions in Templates</h2>
        <p>
          You can insert any valid JavaScript expression inside{" "}
          <code>${"{ }"}</code> — variables, arithmetic, or even ternary
          operators.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 5, b = 10;
console.log(\`Sum = \${a + b}\`);
console.log(\`Product = \${a * b}\`);
console.log(\`You are \${b > a ? "smart" : "learning"}!\`);`}
          />
          <TryItButton
            code={`let a = 5, b = 10;
console.log(\`Sum = \${a + b}\`);
console.log(\`Product = \${a * b}\`);
console.log(\`You are \${b > a ? "smart" : "learning"}!\`);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>
            Sum = 15{"\n"}Product = 50{"\n"}You are smart!
          </pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Function Calls in Templates */}
      <div ref={refs.functions} className="tutorial-section">
        <h2 className="section-title">4️⃣ Function Calls in Templates</h2>
        <p>
          You can even call functions directly inside template literals to
          compute dynamic content.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`function greet(name) {
  return \`Hello, \${name.toUpperCase()}!\`;
}

let message = \`Message: \${greet("world")}\`;
console.log(message);`}
          />
          <TryItButton
            code={`function greet(name) {
  return \`Hello, \${name.toUpperCase()}!\`;
}

let message = \`Message: \${greet("world")}\`;
console.log(message);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>Message: Hello, WORLD!</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Tagged Templates */}
      <div ref={refs.tagged} className="tutorial-section">
        <h2 className="section-title">5️⃣ Tagged Templates</h2>
        <p>
          Tagged templates allow you to parse template literals using a custom
          function before output is returned.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`function highlight(strings, ...values) {
  return strings.reduce((result, str, i) => 
    result + str + (values[i] ? values[i].toUpperCase() : ""), "");
}

let name = "sanjana";
let age = 22;
let result = highlight\`Name: \${name}, Age: \${age}\`;

console.log(result); // Name: SANJANA, Age: 22`}
          />
          <TryItButton
            code={`function highlight(strings, ...values) {
  return strings.reduce((result, str, i) => 
    result + str + (values[i] ? values[i].toUpperCase() : ""), "");
}

let name = "sanjana";
let age = 22;
let result = highlight\`Name: \${name}, Age: \${age}\`;

console.log(result);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>Name: SANJANA, Age: 22</pre>
        </OutputBox>
      </div>

      {/* Footer / Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use template literals for cleaner string concatenation and HTML
            generation.
          </li>
          <li>
            Explore <code>tagged templates</code> for dynamic styling and
            sanitizing user inputs.
          </li>
          <li>
            Combine with <code>map()</code> and <code>join()</code> for dynamic
            list rendering.
          </li>
        </ul>
      </div>
    </div>
  );
}
